import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Tooltip,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useSnackbar } from 'notistack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import api from '../utils/api';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { validateWorkerPlanChange } from '../utils/validateWorkerPlanChange';

// Helper function to get plan ID based on environment
const getPlanPriceId = (plan) => {
  if (!plan) return null;
  const isProduction = process.env.NODE_ENV === 'production';
  
  // Use plan_id in production, plan_id_test in development
  if (isProduction) {
    return plan.plan_id; // Use plan_id for live/production
  } else {
    return plan.plan_id_test || plan.plan_id; // Use plan_id_test for development, fallback to plan_id
  }
};

const BILLING_CYCLES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' }
];

const Subscriptions = () => {
  const stripe = useStripe();
  const elements = useElements();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);

  // Function to format date from ISO to readable format
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return '-';
    }
  };

  // Function to calculate next billing date
  const calculateNextBillingDate = (subscription) => {
    if (!subscription || !subscription.created_at) {
      return '-';
    }
    
    // If subscription is inactive, no next billing date
    if (subscription.status !== 1) {
      return 'N/A';
    }
    
    try {
      const startDate = new Date(subscription.created_at);
      const durationType = subscription.type; // Use 'type' instead of 'duration_type'
      
      let nextBillingDate = new Date(startDate);
      
      if (durationType === 1) { // Monthly
        nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);
      } else if (durationType === 2) { // Yearly
        nextBillingDate.setFullYear(nextBillingDate.getFullYear() + 1);
      } else if (durationType === 3) { // Free plan
        return 'No Billing'; // Free plan has no billing
      } else {
        return '-'; // Unknown duration type
      }
      
      const formattedDate = nextBillingDate.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
      
      return formattedDate;
    } catch (error) {
      console.error('Error calculating next billing date:', error);
      return '-';
    }
  };
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    billing_cycle: 'monthly',
    status: 'active'
  });
  const [clientsDialogOpen, setClientsDialogOpen] = useState(false);
  const [selectedSubscriptionClients, setSelectedSubscriptionClients] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [selectedPlanPrice, setSelectedPlanPrice] = useState('');
    const [selectedPlanPriceId, setSelectedPlanPriceId] = useState('');
  const [amount, setAmount] = useState('$0.00');
  const [noOfWorkers, setNoOfWorkers] = useState(1);
  const [plans, setPlans] = useState([]);
  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: ''
  });
  const [selectedPlanAmount, setSelectedPlanAmount] = useState(0);
  const [workerCount, setWorkerCount] = useState(0);
  const [companyId, setCompanyId] = useState(null);
   
  // Filter out the current plan from available upgrade options
  const availablePlans = React.useMemo(() => {
    if (!Array.isArray(plans)) return [];

    console.log(plans);
    console.log(currentSubscription);
    return plans.filter(plan => {
      // Check if plan has price ID (either test or live based on environment)
      const planPriceId = getPlanPriceId(plan);
      if (!plan || !planPriceId) return false;
      if (!currentSubscription) return true;
      // Check both plan ID and plan_name to ensure we don't show the current plan
      return plan.id !== currentSubscription.id && 
             plan.plan_name !== currentSubscription.name && plan.id !== 41 && plan.id !== 42 && plan.id !== 43;
    });
  }, [plans, currentSubscription]);

  const fetchSubscriptions = async () => {
    try {
      const response = await api.get('/v1/subscriptions');
      setSubscriptions(response.data.data || []);
      // Also set subscription history from this response if current API doesn't have it
      if (response.data.data && response.data.data.length > 0) {
        setSubscriptionHistory(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      enqueueSnackbar('Error fetching subscriptions', { variant: 'error' });
    }
  };

  const fetchSubscriptionData = async () => {
    try {
      const response = await api.get('/v1/subscriptions/current');
      setCurrentSubscription(response.data.data?.current_plan || null);
      setSubscriptionHistory(response.data.data?.subscription_history || []);
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      enqueueSnackbar('Error fetching subscription data', { variant: 'error' });
    }
  }; 

  const fetchPlans = async () => {
    try {
      const response = await api.get('/v1/plans');

      if (response.data && response.data.data) {
        setPlans(response.data.data);
      } else {
        setPlans([]);
        enqueueSnackbar('No plans available', { variant: 'warning' });
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      enqueueSnackbar('Error fetching plans', { variant: 'error' });
      setPlans([]);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setDataLoading(true);
      try {
        // Load subscriptions first
        await fetchSubscriptions();
        // Then load other data
        await Promise.all([
          fetchSubscriptionData(),
          fetchPlans()
        ]);
      } finally {
        setDataLoading(false);
      }
    };
    
    loadData();
  }, []);

  useEffect(() => {
    const company_id = localStorage.getItem('company_id');
    const fetchWorkerCount = async () => {
      try {
        // Use POST instead of GET and pass companyId in the body
        const response = await api.post('/v1/employees/count', { company_id });
        setWorkerCount(response.data.data?.workerCount || 0); // Adjust if your API response is different
   

      } catch (error) {
        console.error('Error fetching worker count:', error);
        setWorkerCount(0);
      }
    };
    //console.log(workerCount);
      fetchWorkerCount();
     // console.log("test");
    //console.log(workerCount);
  }, [companyId]);




  const handleOpenDialog = (subscription = null) => {
    if (subscription) {
      setSelectedSubscription(subscription);
      setFormData({
        name: subscription.name,
        description: subscription.description || '',
        price: subscription.price,
        billing_cycle: subscription.billing_cycle,
        status: subscription.status
      });
    } else {
      setSelectedSubscription(null);
      setFormData({
        name: '',
        description: '',
        price: '',
        billing_cycle: 'monthly',
        status: 'active'
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedSubscription(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      billing_cycle: 'monthly',
      status: 'active'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (selectedSubscription) {
        await api.put(`/v1/subscriptions/${selectedSubscription.id}`, formData);
        enqueueSnackbar('Subscription updated successfully', { variant: 'success' });
      } else {
        await api.post('/v1/subscriptions', formData);
        enqueueSnackbar('Subscription created successfully', { variant: 'success' });
      }
      handleCloseDialog();
      fetchSubscriptions();
    } catch (error) {
      console.error('Error saving subscription:', error);
      enqueueSnackbar('Error saving subscription', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (subscription) => {
    if (!window.confirm('Are you sure you want to delete this subscription?')) {
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/v1/subscriptions/${subscription.id}`);
      enqueueSnackbar('Subscription deleted successfully', { variant: 'success' });
      fetchSubscriptions();
    } catch (error) {
      console.error('Error deleting subscription:', error);
      enqueueSnackbar('Error deleting subscription', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleViewClients = async (subscription) => {
    try {
      setLoading(true);
      const response = await api.get(`/v1/subscriptions/${subscription.id}/clients`);
      setSelectedSubscriptionClients(response.data.data || []);
      setClientsDialogOpen(true);
    } catch (error) {
      console.error('Error fetching subscription clients:', error);
      enqueueSnackbar('Error fetching subscription clients', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handlePlanChange = (event) => {
    const selectedPlanId = event.target.value;
    console.log(selectedPlanId);
    
    // Get the plan_price from the hidden input
    const planPrice = event.target.closest('div').querySelector('input[name="plan_price"]').value;
    console.log("plan_price:", planPrice);

    // Prevent selecting the current plan
    if (currentSubscription && 
        (selectedPlanId === currentSubscription.id || 
         plans.find(p => p.id === selectedPlanId)?.id == currentSubscription.id)) {
      enqueueSnackbar('You cannot upgrade to your current plan', { variant: 'warning' });
      return;
    }
    
    console.log(selectedPlanId); 
    setSelectedPlan(selectedPlanId);
    setSelectedPlanPriceId(planPrice);
    setSelectedPlanPrice(planPrice);
    console.log(plans);
    // Find the selected plan from the plans array
    const selectedPlanDetails = plans.find(plan => plan.id == selectedPlanId);
    console.log(selectedPlanDetails);
    if (selectedPlanDetails) {
      setSelectedPlanAmount(selectedPlanDetails.amount);
      // Use environment-based plan price ID
      const planPriceId = getPlanPriceId(selectedPlanDetails);
      setSelectedPlanPrice(planPriceId);
    }
  };

  useEffect(() => {
    if (selectedPlanAmount && noOfWorkers) {
      setAmount(`$${(selectedPlanAmount * noOfWorkers).toFixed(2)}`);
    }
  }, [selectedPlanAmount, noOfWorkers]);

  const handleCardDetailsChange = (field) => (event) => {
    setCardDetails(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  }; 
useEffect(() => {
  console.log('workerCount updated:', workerCount);
}, [workerCount]);

  const handleUpgradePlan = async () => {
    try {
      setLoading(true);
      const totalAmount = selectedPlanAmount * noOfWorkers;
         
         console.log("test2");
    console.log(workerCount);
    console.log(parseInt(selectedPlan));
    console.log(currentSubscription?.id)
    console.log("test");
      
      if (!validateWorkerPlanChange(currentSubscription, selectedPlan, noOfWorkers, workerCount, enqueueSnackbar, setLoading)) 
      {
      return false;
       }
console.log(noOfWorkers);
console.log(workerCount);

//return false;
      // Create payment method if Stripe is available (keeps existing fields intact)
      let paymentMethodId = undefined;
      if (stripe && elements) {
        const cardElement = elements.getElement(CardElement);
        if (cardElement) {
          const { error, paymentMethod } = await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
          });
          if (error) {
            console.error('Stripe PaymentMethod error:', error);
            enqueueSnackbar(error.message || 'Invalid card details', { variant: 'error' });
            setLoading(false);
            return;
          }
          paymentMethodId = paymentMethod?.id;
        }
      }

      const response = await api.post('/v1/subscriptions/update', {
        current_plan_id: currentSubscription?.id,
        no_of_workers: noOfWorkers,
        plan_id: selectedPlan,
        plan_price: selectedPlanPriceId,
        // Send required payment_method_id in addition to existing token object
        ...(paymentMethodId ? { payment_method_id: paymentMethodId } : {}),
        stripe_token: {
          number: cardDetails.cardNumber,
          exp_month: parseInt(cardDetails.expiryMonth),
          exp_year: parseInt(cardDetails.expiryYear),
          cvc: cardDetails.cvv
        }
      });

      if (response.data.message === 'Subscription updated successfully') {
        enqueueSnackbar('Plan updated successfully', { variant: 'success' });
        handleCloseDialog();
        fetchSubscriptionData();
      }
    } catch (error) {
      console.error('Error updating plan:', error);
      enqueueSnackbar(error.response?.data?.message || 'Error updating plan', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (dataLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 0, sm: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: { xs: 2, sm: 0 },
        mb: { xs: 2, sm: 3 },
        px: { xs: 2, sm: 0 }
      }}>
        <Typography variant="h4" sx={{ 
          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
          fontWeight: '700',
          color: '#4e0159',
          letterSpacing: '-0.5px',
          mb: 0
        }}>
          Subscriptions
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          fullWidth={isMobile}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Add Subscription
        </Button>
      </Box>

      

      {/* Current Plan Info */}
      <Paper sx={{ 
        p: { xs: 2, sm: 3 }, 
        mb: { xs: 2, sm: 3 },
        mx: { xs: 2, sm: 0 },
        backgroundColor: 'white',
        borderRadius: { xs: 0, sm: 1 },
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <Typography variant="h6" sx={{ mb: 2, color: '#4e0159', fontWeight: 600 }}>Current Plan Details</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Plan Name</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {currentSubscription?.name || 'Self Free'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Status</Typography>
            <Chip
              label={currentSubscription?.status === 'active' ? 'Active' : 'Inactive'}
              color={currentSubscription?.status === 'active' ? 'success' : 'default'}
              size="small"
              sx={{ mt: 0.5 }}
            />
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Start Date</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {currentSubscription?.start_date || '-'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Next Billing Date</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {currentSubscription?.end_date || '-'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Subscription Duration</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {(() => {
                // Find duration type from subscription history for current plan
                const currentPlanHistory = subscriptionHistory.find(sub => sub.plan_name === currentSubscription?.name);
                if (currentPlanHistory) {
                  return currentPlanHistory.type === 1 ? 'Monthly' : 
                         currentPlanHistory.type === 2 ? 'Yearly' : 
                         currentPlanHistory.type === 3 ? 'Free' : 'Unknown';
                }
                return '-';
              })()}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Subscription History */}
      <Paper sx={{ 
        p: { xs: 1.5, sm: 2 },
        mx: { xs: 2, sm: 0 },
        borderRadius: { xs: 0, sm: 1 },
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <Typography variant="h6" sx={{ mb: 2, color: '#4e0159', fontWeight: 600 }}>Subscription History</Typography>
        <TableContainer>
          <Table>
            <TableHead
              sx={{
                '& .MuiTableCell-head': {
                  backgroundColor: '#4e0159',
                  color: '#ffffff',
                }
              }}
            >
              <TableRow>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 0,
                  paddingRight: 1
                }}>S.No</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Plan Name</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Amount</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Total Amount</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Duration Type</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>No. of Workers</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Status</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 2
                }}>Created At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subscriptionHistory.map((sub, index) => (
                <TableRow key={sub.id} hover>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{sub.plan_name}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>${Number(sub.amount).toFixed(2)}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>
                    ${(Number(sub.amount) * Number(sub.num_emp || 0)).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    {sub.type === 1 ? 'Monthly' : 
                     sub.type === 2 ? 'Yearly' : 
                     sub.type === 3 ? 'Free' : 'Unknown'}
                  </TableCell>
                  <TableCell>{sub.num_emp || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={sub.status === 1 ? 'Active' : 'Inactive'}
                      color={sub.status === 1 ? 'success' : 'default'}
                      size="small"
                      sx={{ 
                        fontWeight: 500,
                        minWidth: '80px'
                      }}
                    />
                  </TableCell>
                  <TableCell>{formatDate(sub.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Subscription Form Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedSubscription ? 'Edit Subscription' : 'Add Subscription'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              label="Name"
              fullWidth
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
            />
            <TextField
              label="Price"
              fullWidth
              required
              type="number"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              margin="normal"
            />
            <TextField
              select
              label="Billing Cycle"
              fullWidth
              required
              value={formData.billing_cycle}
              onChange={(e) => setFormData({ ...formData, billing_cycle: e.target.value })}
              margin="normal"
            >
              {BILLING_CYCLES.map((cycle) => (
                <MenuItem key={cycle.value} value={cycle.value}>
                  {cycle.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Status"
              fullWidth
              required
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              margin="normal"
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Subscription Clients Dialog */}
      <Dialog
        open={clientsDialogOpen}
        onClose={() => setClientsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Subscription Clients</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead
                sx={{
                  '& .MuiTableCell-head': {
                    backgroundColor: '#4e0159',
                    color: '#ffffff',
                  }
                }}
              >
                <TableRow>
                  <TableCell>Company Name</TableCell>
                  <TableCell>Client Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedSubscriptionClients.map((client) => (
                  <TableRow key={client.id}>
                    <TableCell>{client.client_company_name}</TableCell>
                    <TableCell>{client.client_name}</TableCell>
                    <TableCell>
                      <Chip
                        label={client.status}
                        color={client.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{new Date(client.start_date).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {client.end_date ? new Date(client.end_date).toLocaleDateString() : '-'}
                    </TableCell>
                  </TableRow>
                ))}
                {selectedSubscriptionClients.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No clients found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClientsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Upgrade Plan Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Upgrade Plan</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>Select your plan:</Typography>
            <RadioGroup value={selectedPlan} onChange={(e) => handlePlanChange(e)}>
              {availablePlans.map((plan) => {
                const planPriceId = getPlanPriceId(plan);
                return (
                  <div key={plan.id}>
                    <input type="hidden" name="plan_price" value={planPriceId} />
                    <FormControlLabel
                      value={plan.id}
                      control={
                        <Radio
                          sx={{
                            '&.Mui-checked': {
                              color: '#4e0159'
                            }
                          }}
                        />
                      }
                      label={`${plan.plan_name} - $${plan.amount.toFixed(2)}`}
                    />
                  </div>
                );
              })}
            </RadioGroup>
            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Amount: {amount}
            </Typography>
          </Box>

          <TextField
            fullWidth
            type="number"
            label="Number of Workers"
            value={noOfWorkers}
            onChange={(e) => setNoOfWorkers(Math.max(1, parseInt(e.target.value) || 1))}
            inputProps={{ min: 1 }}
            sx={{ mb: 2 }}
          />

          <Typography variant="subtitle1" sx={{ mb: 2 }}>Card Details</Typography>
          {/* Stripe CardElement (added) - existing manual fields retained */}
          <Box sx={{ mb: 2, p: 1, border: '1px solid #ccc', borderRadius: 1 }}>
            <CardElement options={{ hidePostalCode: true }} />
          </Box>
          {/* Manual TextFields removed; CardElement will capture all card data */}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseDialog}
            sx={{
              color: '#4e0159',
              '&:hover': {
                color: '#4e0159',
                opacity: 0.8,
                backgroundColor: 'rgba(78, 1, 89, 0.1)'
              }
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpgradePlan}
            disabled={loading || !selectedPlan}
          >
            {loading ? <CircularProgress size={24} /> : 'Upgrade Current Plan'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Subscriptions;